// src/components/AddAutomationRuleForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  TextField,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BoltIcon from "@mui/icons-material/Bolt";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";

import DynamicFields from "./DynamicFields";
import ScheduleRunWindowField from "./ScheduleRunWindowField";
import AutomationEventPicker from "./AutomationEventPicker";
import AutomationConditionPickerDialog from "./AutomationConditionPickerDialog";
import AutomationRuleAiSuggestDialog from "./AutomationRuleAiSuggestDialog";
import { useAutomationCatalog } from "../hooks/useAutomationRules";
import { findEnrichedEvent } from "../utils/automationEventCatalog";
import {
  isClientFieldConditionKey,
  listAvailableClientFieldPresets,
  mergeClientFieldConditionsIntoEventSchema,
} from "../utils/automationClientFieldCatalog";
import { mergeTagConditionsIntoEventSchema } from "../utils/automationTagMatch";
import {
  buildConditionPickerGroups,
  getConditionAccordionTitle,
} from "../utils/automationConditionCatalog";
import {
  ASSIGNEE_FILTER_FIELD,
  LEGACY_CONDITION_AGENT_KEY,
  LEGACY_CONDITION_USER_KEY,
  normalizeCondicoesAssigneeFilter,
} from "../utils/assigneeFilterField";
import { useAuthContext } from "../context/AuthContext";
import { fetchData } from "../api/client";
import { mergeAiSuggestionIntoForm } from "../utils/automationAiSuggest";
import {
  formatAutomationDisplayValue,
  formatScheduleIntervalLabel,
  isScheduleIntervalValue,
} from "../utils/automationLabelFormat";
import {
  defaultScheduleRunWindow,
  formatScheduleRunWindowLabel,
  isScheduleConditionMetaKey,
  normalizeScheduleRunWindow,
} from "../utils/scheduleRunWindow";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

function extractCampaignList(response) {
  const raw = response?.data ?? response;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.campaigns)) return raw.campaigns;
  return [];
}


const steps = ["Objeto", "Evento", "Condições", "Ações", "Resumo"];

/* ================= Helpers UI (chips) ================= */
const getImpactAlert = (count, scope) => {
  const capped = count >= 51;
  const shown = capped ? "mais de 50" : String(count);
  if (scope === "inactivity" || scope === "awaiting") {
    const who = scope === "awaiting"
      ? "solicitações Pendentes criadas nos últimos 20 minutos"
      : "conversas mexidas nos últimos 20 minutos";
    if (count === 0) {
      return {
        severity: "info",
        title: "Nenhuma entra agora",
        text: `Nenhuma das ${who} bate com o filtro. Depois de ligada, a regra vale para as próximas. Conversas mais antigas não entram.`,
      };
    }
    return {
      severity: count > 50 ? "error" : count > 10 ? "warning" : "info",
      title: count > 50 ? `${shown} conversas na janela recente` : `${shown} conversas na janela recente`,
      text: `Há ${shown} ${who} neste filtro. Esse número não é o histórico inteiro. Quem está parado há mais tempo não entra. Quem ainda não completou o prazo só dispara quando o tempo acabar, com atraso de até 1 minuto. Na primeira hora a regra pausa depois de 20 ações. O que a ação fizer não volta atrás.`,
    };
  }

  if (count === 0) {
    return {
      severity: "info",
      title: "Nenhuma conversa bate com o filtro agora",
      text: "Este evento só age na conversa que disparar o momento. Criar a regra não percorre as que já existem. Se nenhuma bate agora, as próximas é que serão avaliadas, uma a uma.",
    };
  }

  return {
    severity: count > 50 ? "warning" : "info",
    title: `${shown} conversas batem com o filtro`,
    text: `Esse número é o tamanho do filtro, não um disparo em massa. A regra só age na conversa que disparar o evento. As outras ${shown} não são alteradas ao salvar.`,
  };
};

const compactText = (text, maxLen = 80) => {
  const s = String(text ?? "");
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen - 1) + "…";
};

const compactListText = (value, maxItems = 2, maxLen = 80) => {
  const arr = Array.isArray(value)
    ? value.map(formatAutomationDisplayValue).filter(Boolean)
    : String(formatAutomationDisplayValue(value) || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  if (arr.length === 0) return "";
  const head = arr.slice(0, maxItems).join(", ");
  const tail = arr.length > maxItems ? ` (+${arr.length - maxItems})` : "";
  return compactText(head + tail, maxLen);
};

const chipSx = {
  maxWidth: "100%",
  "& .MuiChip-label": {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
};

/* ======== Resumo ao vivo ======== */
function LivePreview({ objeto, evento, condicoesLabels, acoesTitles, eventSchema }) {

  return (
    <Card
      elevation={8}
      sx={{
        position: "sticky",
        top: 16,
        borderRadius: 2,
        boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
        border: "1px solid",
        borderColor: "divider",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.98))",
        backdropFilter: "blur(6px)",
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Resumo ao vivo
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          <InfoOutlinedIcon fontSize="inherit" />
          Pré-visualização
        </Typography>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2">Objeto</Typography>
          <Chip label={objeto || "—"} size="small" sx={{ mt: 0.5 }} />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Evento</Typography>
          <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
            <Chip label={evento || "—"} size="small" />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Condições</Typography>
          <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {condicoesLabels && Object.keys(condicoesLabels).length > 0 ? (
              Object.entries(condicoesLabels).map(([k, v]) => {
                const niceKey = eventSchema?.[k]?.label || k;
                const display = formatAutomationDisplayValue(v);
                const full = `${niceKey}: ${display}`;
                const shown = `${niceKey}: ${compactListText(v, 2, 90) || display || "—"}`;

                return (
                  <Tooltip key={k} title={full}>
                    <Chip label={shown} size="small" sx={chipSx} />
                  </Tooltip>
                );

              })
            ) : (
              <Chip label="Nenhuma" size="small" />
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Ações</Typography>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {acoesTitles.length === 0 ? (
              <Chip label="Nenhuma" size="small" />
            ) : (
              acoesTitles.map((t, i) => (
                <Box
                  key={`${t}-${i}`}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    fontSize: 14,
                  }}
                >
                  {i + 1}. {t}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function normalizeStoredActions(cfg, rule) {
  if (Array.isArray(cfg?.acoes)) return cfg.acoes;
  if (cfg?.acoes && typeof cfg.acoes === "object") {
    const id = Number(rule?.id_acao) || 0;
    if (!id) return [];
    return [{ id, values: cfg.acoes, labels: cfg.acoes_labels || {} }];
  }
  return [];
}

export default function AddAutomationRuleForm({
  rule,
  onClose,
  onSubmit,
  isFetching,
  isLoading,
  initialAiSuggestion = null,
}) {
  const { t } = useTranslation("settings");
  const { userData } = useAuthContext();

  // ===== Estado base
  const initial = useMemo(() => {
    let cfg = {};
    if (rule?.config) {
      try {
        cfg = JSON.parse(rule.config);
      } catch {}
    }
    return {
      id: rule?.id || null,
      descricao: rule?.descricao || "",
      id_objeto: cfg.objeto ?? rule?.id_objeto ?? "",
      id_condicao: cfg.evento ?? rule?.id_condicao ?? "",
      prioridade: cfg.prioridade ?? "",
      fila: rule?.fila ?? cfg.extras?.fila ?? "",
      ordem: rule?.ordem ?? cfg.extras?.ordem ?? "",
      break: rule?.break ?? cfg.extras?.break ?? 0,
      webhook: rule?.webhook ?? cfg.extras?.webhook ?? "",
      token: rule?.token ?? cfg.extras?.token ?? "",
      condicoes: cfg.condicoes ?? {},
      condicoes_labels: cfg.condicoes_labels ?? {},
      acoes: normalizeStoredActions(cfg, rule),
      ligarAgora: rule?.id ? Number(rule?.ativa ?? 1) !== 0 : false,
      id_usuario: rule?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
    };
  }, [rule, userData?.id]);

  const [form, setForm] = useState(initial);
  const lastRuleIdRef = useRef(null);

  useEffect(() => {
    const incomingId = initial?.id ?? null;

    // Só re-hidrata quando mudar a regra (ex.: abriu outra ou carregou a primeira)
    if (incomingId !== lastRuleIdRef.current) {
      lastRuleIdRef.current = incomingId;
      setForm(initial);
      setActiveStep(0);
      setErrors({});
      setErrorMessage(null);
      setAiSuggested(Boolean(initialAiSuggestion));
      setAiMeta(initialAiSuggestion?.meta || null);
    }
  }, [initial, initialAiSuggestion]);

  useEffect(() => {
    if (!initialAiSuggestion?.suggestion || initial?.id) return;
    setForm((f) => mergeAiSuggestionIntoForm(f, initialAiSuggestion.suggestion, { userId: userData?.id }));
    setAiSuggested(true);
    setAiMeta(initialAiSuggestion.meta || null);
    setActiveStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAiSuggestion]);

  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiSuggested, setAiSuggested] = useState(Boolean(initialAiSuggestion));
  const [aiMeta, setAiMeta] = useState(initialAiSuggestion?.meta || null);

  const topRef = useRef(null);

  const [previewCount, setPreviewCount] = useState(null);
  const [previewScope, setPreviewScope] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);


  useEffect(() => {
    if (activeStep !== 2) return;
    if (!form.id_objeto || !form.id_condicao) return;

    const filterKeys = Object.keys(form.condicoes || {}).filter(
      (k) => !isScheduleConditionMetaKey(k)
    );
    const scheduled = form.condicoes?.schedule_interval !== undefined;
    if (!filterKeys.length && !scheduled) {
      setPreviewCount(null);
      setPreviewScope(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setPreviewLoading(true);

        const previewCondicoes = { ...form.condicoes };
        delete previewCondicoes.schedule_interval;
        delete previewCondicoes.schedule_run_window;
        delete previewCondicoes.schedule_apply_all;
        delete previewCondicoes.schedule_include_closed;
        delete previewCondicoes.schedule_confirm_wide;

        const res = await fetchData(
          "/automation-rules/preview",
          "POST",
          {
            id_objeto: Number(form.id_objeto),
            id_condicao: Number(form.id_condicao),
            condicoes: previewCondicoes,
          }
        );

        if (res?.data?.scope === "unavailable" || res?.data?.total == null) {
          setPreviewCount(null);
          setPreviewScope(res?.data?.scope || "unavailable");
        } else {
          setPreviewCount(res?.data?.total ?? 0);
          setPreviewScope(res?.data?.scope || null);
        }
      } catch {
        setPreviewCount(null);
        setPreviewScope(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 500); // debounce

    return () => clearTimeout(timer);
  }, [activeStep, form.id_objeto, form.id_condicao, form.condicoes]);

  // catálogos
  const { data: metaAll } = useAutomationCatalog();
  const { data: metaByObj } = useAutomationCatalog(form.id_objeto || undefined);

  const objetos = metaAll?.data?.objetos ?? [];
  const eventos = (metaByObj?.data?.condicoes ?? []).filter(
    (c) => c.id_objeto === Number(form.id_objeto)
  );
  const acoesCat = (metaByObj?.data?.acoes ?? []).filter(
    (a) => a.id_objeto === Number(form.id_objeto)
  );

  const selectedObjeto = objetos.find((o) => o.id === Number(form.id_objeto));
  const selectedEvento = eventos.find((e) => e.id === Number(form.id_condicao));
  const selectedEventoEnriched = useMemo(
    () => findEnrichedEvent(eventos, form.id_condicao),
    [eventos, form.id_condicao]
  );

  const handleSelectEvento = (evtId) => {
    const evt = eventos.find((c) => c.id === Number(evtId));
    const evtForm = evt?.form?.condicoes || evt?.form?.fields || evt?.form || {};
    const hasSchedule = Boolean(evtForm?.schedule_interval);
    const runWindow = defaultScheduleRunWindow();
    setForm((f) => ({
      ...f,
      id_condicao: evtId,
      condicoes: hasSchedule
        ? {
            schedule_interval: { value: "", unit: "minutes" },
            schedule_run_window: runWindow,
          }
        : {},
      condicoes_labels: hasSchedule
        ? { schedule_run_window: formatScheduleRunWindowLabel(runWindow) }
        : {},
    }));
  };

  // ===== schema do evento (para filtros)
  const eventSchema = useMemo(() => {
    const raw =
      selectedEvento?.form?.condicoes ||
      selectedEvento?.form?.fields ||
      selectedEvento?.form ||
      {};
    if (Number(form.id_objeto) !== 1) return raw;
    return mergeTagConditionsIntoEventSchema(
      mergeClientFieldConditionsIntoEventSchema(raw)
    );
  }, [selectedEvento, form.id_objeto]);

  const needsCampaignOptions = Number(form.id_objeto) === 1 && Boolean(eventSchema?.campanhas);
  const needsLegacyWaCampaignOptions =
    Number(form.id_objeto) === 1 && Boolean(eventSchema?.campanhas_whatsapp);

  const { data: broadcastsResp, isLoading: loadingBroadcasts } = useQuery(
    ["automationCampaignBroadcasts"],
    () => fetchData("/campaigns/broadcasts", "GET"),
    {
      enabled: needsCampaignOptions,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );
  const { data: waCampaignsResp, isLoading: loadingWaCampaigns } = useQuery(
    ["automationWhatsappCampaigns"],
    () => fetchData("/campaigns/whatsapp", "GET"),
    {
      enabled: needsLegacyWaCampaignOptions,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );

  const campaignConditionHiddenKeys = useMemo(() => {
    const hidden = new Set();
    if (needsCampaignOptions) {
      const list = extractCampaignList(broadcastsResp);
      if (loadingBroadcasts || list.length === 0) hidden.add("campanhas");
    }
    if (needsLegacyWaCampaignOptions) {
      const list = extractCampaignList(waCampaignsResp);
      if (loadingWaCampaigns || list.length === 0) hidden.add("campanhas_whatsapp");
    }
    return hidden;
  }, [
    needsCampaignOptions,
    needsLegacyWaCampaignOptions,
    broadcastsResp,
    waCampaignsResp,
    loadingBroadcasts,
    loadingWaCampaigns,
  ]);

  const isScheduledEvent = Boolean(eventSchema?.schedule_interval);
  const allFilterEntries = Object.entries(eventSchema).filter(
    ([k]) => !isScheduleConditionMetaKey(k)
  );

  // Garante janela de execução + label ao editar regras antigas sem o campo.
  useEffect(() => {
    if (!isScheduledEvent) return;
    setForm((f) => {
      const cond = f.condicoes || {};
      if (cond.schedule_interval === undefined) return f;
      const hasWindow = cond.schedule_run_window != null;
      const normalized = normalizeScheduleRunWindow(cond.schedule_run_window);
      const label = formatScheduleRunWindowLabel(normalized);
      const currentLabel = f.condicoes_labels?.schedule_run_window;
      if (hasWindow && currentLabel === label) return f;
      return {
        ...f,
        condicoes: { ...cond, schedule_run_window: normalized },
        condicoes_labels: {
          ...(f.condicoes_labels || {}),
          schedule_run_window: label,
        },
      };
    });
  }, [isScheduledEvent, form.id_condicao]);
  const usedKeys = useMemo(() => {
    const keys = new Set(Object.keys(form.condicoes || {}));
    if (eventSchema?.[ASSIGNEE_FILTER_FIELD]) {
      if (keys.has(LEGACY_CONDITION_USER_KEY) || keys.has(LEGACY_CONDITION_AGENT_KEY)) {
        keys.add(ASSIGNEE_FILTER_FIELD);
      }
    }
    return keys;
  }, [form.condicoes, eventSchema]);
  const remainingFilters = allFilterEntries.filter(
    ([k]) =>
      !usedKeys.has(k) &&
      !isClientFieldConditionKey(k) &&
      !campaignConditionHiddenKeys.has(k)
  );
  const remainingClientFieldPresets = listAvailableClientFieldPresets(usedKeys);
  const conditionPickerGroups = useMemo(
    () =>
      buildConditionPickerGroups({
        eventSchema,
        usedKeys,
        hiddenKeys: campaignConditionHiddenKeys,
      }),
    [eventSchema, usedKeys, campaignConditionHiddenKeys]
  );
  const hasConditionPickerOptions =
    remainingFilters.length > 0 ||
    remainingClientFieldPresets.length > 0 ||
    conditionPickerGroups.some(
      (g) => g.schemaItems.length > 0 || g.clientGroups.length > 0
    );

  const DYNAMIC_KEYS = new Set([
    "origensdeleads",
    "usuarios",
    "responsavel",
    "agentesdeia",
    "agentes",
    "formasdepagamento",
    "tiposdeleads",
    "subtiposdeleads",
    "subtiposdeleads1",
    "subtiposdeleads2",
    "metodosdeentrega",
    "cargos",
    "positions",
  ]);

  const isDynamicObjectField = (def, key) => {
    if (DYNAMIC_KEYS.has(key)) return true;
    if (def?.type === "assignee_filter" || def?.type === "assignee_transfer") return true;
    return [
      "origensdeleads",
      "usuarios",
      "agentesdeia",
      "agentes",
      "formasdepagamento",
      "tiposdeleads",
      "subtiposdeleads",
      "metodosdeentrega",
      "cargos",
      "positions",
    ].includes(def?.object);
  };


  useEffect(() => {
    // precisa ter schema e condições carregadas
    const cond = form?.condicoes || {};
    if (!eventSchema || !Object.keys(eventSchema).length) return;
    if (!cond || !Object.keys(cond).length) return;

    let changed = false;
    const nextLabels = { ...(form.condicoes_labels || {}) };

    Object.entries(cond).forEach(([key, value]) => {
      const def = eventSchema[key];
      if (!def) return;

      if (isDynamicObjectField(def, key)) {
        // não grava label fallback com IDs — deixa pro DynamicFields rehidratar
        return;
      }

      if (isEmptyLabel(nextLabels[key])) {
        const computed = labelFromValue(def, value);
        if (!isEmptyLabel(computed)) {
          nextLabels[key] = computed;
          changed = true;
        }
      }

    });

    if (changed) {
      setForm((f) => ({ ...f, condicoes_labels: nextLabels }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSchema, form?.condicoes]);

  useEffect(() => {
    if (!eventSchema?.[ASSIGNEE_FILTER_FIELD]) return;
    const normalized = normalizeCondicoesAssigneeFilter(form.condicoes || {}, eventSchema);
    if (JSON.stringify(normalized) === JSON.stringify(form.condicoes || {})) return;
    setForm((f) => {
      const nextLabels = { ...(f.condicoes_labels || {}) };
      delete nextLabels[LEGACY_CONDITION_USER_KEY];
      delete nextLabels[LEGACY_CONDITION_AGENT_KEY];
      return { ...f, condicoes: normalized, condicoes_labels: nextLabels };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSchema, form?.condicoes]);


  // ===== utils / validação
  const isEmpty = (v) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

  const isEmptyLabel = (v) =>
    v == null ||
    (typeof v === "string" && (v.trim() === "" || v === "[object Object]")) ||
    (typeof v === "object" && !Array.isArray(v)) ||
    (Array.isArray(v) && v.length === 0);

  const normalizeOptions = (def) => {
    // tenta cobrir formatos comuns
    const raw =
      def?.options ||
      def?.items ||
      def?.choices ||
      def?.values ||
      def?.enum ||
      [];

    if (Array.isArray(raw)) {
      return raw.map((it) => {
        if (it && typeof it === "object") {
          const value = it.value ?? it.id ?? it.key ?? it.code ?? it;
          const label = it.label ?? it.nome ?? it.name ?? it.title ?? String(value);
          return { value, label };
        }
        return { value: it, label: String(it) };
      });
    }

    // caso venha como objeto {value: label}
    if (raw && typeof raw === "object") {
      return Object.entries(raw).map(([value, label]) => ({ value, label: String(label) }));
    }

    return [];
  };

  const toArrayValue = (value) => {
    if (value == null) return [];
    if (Array.isArray(value)) return value;

    // se vier string tipo "9,8,7" ou "9;8;7"
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return [];
      if (s.includes(",") || s.includes(";")) {
        return s
          .split(/[;,]/g)
          .map((t) => t.trim())
          .filter(Boolean);
      }
      return [s];
    }

    // number/bool/etc
    return [value];
  };

  const labelFromValue = (def, value) => {
    if (def?.type === "schedule_interval" || isScheduleIntervalValue(value)) {
      return formatScheduleIntervalLabel(value);
    }

    const opts = normalizeOptions(def);

    const findOne = (v) => {
      const hit = opts.find((o) => String(o.value) === String(v));
      return hit ? hit.label : formatAutomationDisplayValue(v) || String(v);
    };

    const arr = toArrayValue(value);

    // se o campo NÃO é múltiplo e veio só 1 valor, devolve string simples
    if (!def?.multiple && arr.length <= 1) return arr.length ? findOne(arr[0]) : "";

    // múltiplo -> devolve string com nomes
    return arr.map(findOne).join(", ");
  };


  const validateStep = (stepIdx) => {
    const e = {};
    const msgs = [];

    if (stepIdx === 0) {
      if (!form.descricao) {
        e.descricao = "Informe uma descrição.";
        msgs.push('Falta "Descrição".');
      }
      if (!form.id_objeto) {
        e.id_objeto = "Selecione o objeto.";
        msgs.push('Falta selecionar o "Objeto".');
      }
    }

    if (stepIdx === 1) {
      if (!form.id_condicao) {
        e.id_condicao = "Selecione o evento.";
        msgs.push('Falta selecionar o "Evento".');
      }
    }

    if (stepIdx === 2) {
      const cond = form.condicoes || {};
      const keys = Object.keys(cond);
      const nonScheduleKeys = keys.filter((k) => !isScheduleConditionMetaKey(k));

      if (isScheduledEvent) {
        const si = cond.schedule_interval;
        const siVal = typeof si === "object" ? Number(si?.value || 0) : Number(si || 0);
        if (!siVal || siVal <= 0) {
          msgs.push("Informe o intervalo de tempo.");
        } else {
          const unit = typeof si === "object" ? si?.unit || "minutes" : "minutes";
          const minVal = unit === "minutes" ? 5 : 1;
          if (siVal < minVal) {
            msgs.push(`O intervalo mínimo é ${minVal} ${unit === "minutes" ? "minutos" : unit === "hours" ? "hora(s)" : "dia(s)"}.`);
          }
        }
        const runWindow = normalizeScheduleRunWindow(cond.schedule_run_window);
        if (runWindow.mode === "defined_schedule") {
          if (!runWindow.weekdays?.length) {
            msgs.push("Selecione ao menos um dia da semana no horário definido.");
          }
          if (!runWindow.start || !runWindow.end) {
            msgs.push("Informe início e fim do horário definido.");
          }
        }
        const hasFilter = nonScheduleKeys.some((key) => {
          const value = cond[key];
          if (value == null || value === "") return false;
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === "object") {
            if (Array.isArray(value.tags)) return value.tags.length > 0;
            return value.value != null && value.value !== "";
          }
          return true;
        });
        const applyAll = Boolean(cond.schedule_apply_all);
        const confirmed = Boolean(cond.schedule_confirm_wide);
        const closed = [2, 4, 5];
        const statusValues = Array.isArray(cond.statuslead) ? cond.statuslead.map(Number) : [];
        if (!hasFilter && !applyAll) {
          msgs.push("Escolha um filtro ou marque que a regra vale para todas as conversas abertas.");
        }
        if ((applyAll || (previewCount != null && previewCount > 50)) && !confirmed) {
          msgs.push("Confirme que a regra pode atingir várias conversas.");
        }
        if (statusValues.some((status) => closed.includes(status)) && !cond.schedule_include_closed) {
          msgs.push("Para incluir concluída, perdida ou cancelada, marque essa opção.");
        }
        if (previewLoading) {
          msgs.push("Espere o cálculo de quantas conversas entram agora.");
        }
        if (previewScope === "unavailable") {
          msgs.push("Não foi possível calcular o alcance. Tente de novo antes de salvar.");
        }
      } else if (nonScheduleKeys.length === 0) {
        msgs.push("Adicione ao menos um filtro para continuar.");
      }

      Object.entries(eventSchema).forEach(([key, def]) => {
        if (isScheduleConditionMetaKey(key)) return;
        if (!(key in cond)) return;
        if (def?.required && isEmpty(cond[key])) {
          const label = def?.label || key;
          msgs.push(`Preencha o filtro obrigatório "${label}".`);
        }
      });
    }

    if (stepIdx === 3) {
      if (!form.acoes?.length) {
        e.acoes = "Inclua ao menos uma ação.";
        msgs.push("Inclua ao menos uma ação.");
      } else {
        form.acoes.forEach((a, idx) => {
          const def = acoesCat.find((x) => x.id === Number(a.id));
          const title = def?.nome || `Ação #${a.id || "?"}`;
          const schemaA = def?.form?.fields || def?.form?.acoes || def?.form || {};
          Object.entries(schemaA).forEach(([k, f]) => {
            if (f?.required && isEmpty(a.values?.[k])) {
              const label = f?.label || k;
              msgs.push(`Ação ${idx + 1} (${title}): preencha "${label}".`);
            }
          });
        });
      }
    }

    setErrors(e);
    setErrorMessage(msgs.length ? msgs.join("\n") : null);
    return !Object.keys(e).length && msgs.length === 0;
  };

  // ===== navegação
  const tryGoTo = (nextIdx) => {
    const ok = validateStep(activeStep);
    if (!ok) {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setErrorMessage(null);
    setActiveStep(nextIdx);
  };

  const handleNext = () => {
    if (activeStep >= steps.length - 1) return;
    tryGoTo(activeStep + 1);
  };
  const handleBack = () => {
    if (activeStep === 0) return;
    setActiveStep((s) => Math.max(0, s - 1));
    setErrorMessage(null);
  };

  // ===== salvar (só no clique)
  const handleSubmit = async () => {
    // valida todas as etapas antes de salvar
    const okAll =
      validateStep(0) && validateStep(1) && validateStep(2) && validateStep(3);
    if (!okAll) {
      if (errors.id_objeto || errors.descricao) setActiveStep(0);
      else if (errors.id_condicao) setActiveStep(1);
      else if (errors.acoes) setActiveStep(3);
      else setActiveStep(2);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const payload = {
      id: form.id || undefined,
      descricao: form.descricao,
      id_usuario: form.id_usuario,
      id_usuario_ultima_alteracao: form.id_usuario_ultima_alteracao,
      id_objeto: Number(form.id_objeto),
      id_condicao: Number(form.id_condicao),
      prioridade: form.prioridade ? Number(form.prioridade) : null,
      fila: form.fila ? Number(form.fila) : null,
      ordem: form.ordem ? Number(form.ordem) : null,
      break: form.break ? Number(form.break) : 0,
      webhook: form.webhook || null,
      token: form.token || null,
      condicoes: form.condicoes || {},
      ativa: form.id
        ? (form.ligarAgora ? 1 : 0)
        : (isScheduledEvent ? (form.ligarAgora ? 1 : 0) : 1),
      acoes: (form.acoes || []).map((a) => ({
        id: Number(a.id),
        values: a.values || {},
      })),
    };

    try {
      setSubmitting(true);
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao salvar a regra.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyAiSuggestion = (payload) => {
    const suggestion = payload?.suggestion;
    if (!suggestion) return;
    setForm((f) => mergeAiSuggestionIntoForm(f, suggestion, { userId: userData?.id }));
    setAiSuggested(true);
    setAiMeta(payload?.meta || null);
    setActiveStep(0);
    setErrors({});
    setErrorMessage(null);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isNewRule = !form.id;

  const stepMeta = {
    condicoes: Object.keys(form.condicoes || {}).filter((k) => !isScheduleConditionMetaKey(k)).length,
    acoes: form.acoes?.length || 0,
  };

  // picker de filtros
  const [conditionPickerOpen, setConditionPickerOpen] = useState(false);

  const addFilter = (key, def) => {
    const isTagFilter =
      def?.type === "tag" ||
      def?.type === "tag_match" ||
      key === "taglead" ||
      key === "tagcliente";
    const initial = isTagFilter ? { tags: [], mode: "any" } : def?.multiple ? [] : "";
    setForm((f) => ({
      ...f,
      condicoes: { ...(f.condicoes || {}), [key]: initial },
      condicoes_labels: { ...(f.condicoes_labels || {}), [key]: "" },
    }));
  };

  const addClientFieldPreset = (preset) => {
    if (!preset?.key) return;
    setForm((f) => ({
      ...f,
      condicoes: { ...(f.condicoes || {}), [preset.key]: String(preset.value) },
      condicoes_labels: {
        ...(f.condicoes_labels || {}),
        [preset.key]: preset.label,
      },
    }));
  };

  const removeFilter = (key) =>
    setForm((f) => {
      const next = { ...(f.condicoes || {}) };
      const nextL = { ...(f.condicoes_labels || {}) };
      delete next[key];
      delete nextL[key];
      if (key === "responsavel") {
        delete next.usuarios;
        delete next.agentesdeia;
        delete nextL.usuarios;
        delete nextL.agentesdeia;
      }
      return { ...f, condicoes: next, condicoes_labels: nextL };
    });

  // menu "adicionar ação"
  const [anchorAddAction, setAnchorAddAction] = useState(null);
  const openAddAction = Boolean(anchorAddAction);
  const handleOpenAddAction = (e) => setAnchorAddAction(e.currentTarget);
  const handleCloseAddAction = () => setAnchorAddAction(null);
  const addAction = (id) => {
    setForm((f) => ({
      ...f,
      acoes: [...(f.acoes || []), { id, values: {}, labels: {} }],
    }));
    handleCloseAddAction();
  };

  const acoesTitles = useMemo(
    () =>
      (form.acoes || []).map((a) => {
        const def = acoesCat.find((x) => x.id === Number(a.id));
        return def?.nome || `Ação #${a.id}`;
      }),
    [form.acoes, acoesCat]
  );

  /* ======================== UI ======================== */
  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div ref={topRef} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          {form.id ? "Editar Regra de Automação" : "Adicionar Regra de Automação"}
        </Typography>
        {isNewRule && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AutoFixHighOutlined />}
            onClick={() => setAiDialogOpen(true)}
            disabled={isFetching || submitting || isLoading}
          >
            {t("automationRuleAi.listButton")}
          </Button>
        )}
      </Box>

      {aiSuggested && (
        <Alert severity="warning" icon={<WarningAmberOutlined />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t("automationRuleAi.formBannerTitle")}
          </Typography>
          <Typography variant="body2" sx={{ mb: aiMeta?.warnings?.length ? 1 : 0 }}>
            {t("automationRuleAi.formBannerBody")}
          </Typography>
          {aiMeta?.warnings?.length ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {aiMeta.warnings.slice(0, 6).map((w) => (
                <Typography component="li" variant="body2" key={w}>
                  {w}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Alert>
      )}

      <AutomationRuleAiSuggestDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        onApply={handleApplyAiSuggestion}
        idObjetoHint={form.id_objeto || null}
        disabled={isFetching || submitting || isLoading}
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
          {errorMessage}
        </Alert>
      )}

      <Grid
        container
        spacing={2}
        sx={{
          maxWidth: "100%",
          overflowX: "hidden",
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* principal */}
        <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label, idx) => {
              let extra = "";
              if (idx === 2 && stepMeta.condicoes) extra = ` • ${stepMeta.condicoes} filtro(s)`;
              if (idx === 3 && stepMeta.acoes) extra = ` • ${stepMeta.acoes} ação(ões)`;
              return (
                <Step key={label}>
                  <StepLabel>
                    {label}
                    {extra}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* IMPORTANTE: sem <form> para não submeter automaticamente */}
          <Box
            onKeyDown={(e) => {
              // Nunca deixa Enter submeter ou acionar "Salvar" implicitamente.
              if (e.key === "Enter") e.preventDefault();
            }}
          >
            {/* ===== 0 - Objeto ===== */}
            {activeStep === 0 && (
              <>
                {isNewRule && !form.descricao && !form.id_objeto && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <span dangerouslySetInnerHTML={{ __html: t("automationRuleAi.formStepHint") }} />
                  </Alert>
                )}
                <Alert icon={<InfoOutlinedIcon />} severity="info" sx={{ mb: 2 }}>
                  Dê um <b>nome</b> para a regra e escolha o <b>Objeto</b> que ela monitora.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descrição"
                      size="small"
                      margin="dense"
                      fullWidth
                      value={form.descricao}
                      onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                      error={!!errors.descricao}
                      helperText={errors.descricao}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Objeto"
                      size="small"
                      margin="dense"
                      fullWidth
                      value={form.id_objeto || ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          id_objeto: e.target.value,
                          id_condicao: "",
                          condicoes: {},
                          condicoes_labels: {},
                          acoes: [],
                        }))
                      }
                      error={!!errors.id_objeto}
                      helperText={errors.id_objeto}
                      required
                    >
                      {objetos.map((o) => (
                        <MenuItem key={o.id} value={o.id}>
                          {o.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </>
            )}

            {/* ===== 1 - Evento ===== */}
            {activeStep === 1 && (
              <>
                {!form.id_objeto ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Selecione um objeto na etapa anterior para ver os eventos disponíveis.
                  </Alert>
                ) : eventos.length === 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Nenhum evento cadastrado para este objeto.
                  </Alert>
                ) : (
                  <AutomationEventPicker
                    eventos={eventos}
                    value={form.id_condicao}
                    onChange={handleSelectEvento}
                    error={errors.id_condicao}
                  />
                )}
              </>
            )}

            {/* ===== 2 - Condições ===== */}
            {activeStep === 2 && (
              <>
                <Alert icon={<TuneIcon />} severity="info" sx={{ mb: 2 }}>
                  {isScheduledEvent ? (
                    <>
                      {selectedEventoEnriched?.meta?.description}{" "}
                      Concluída, perdida e cancelada ficam de fora, salvo se você marcar incluir encerradas.
                      A regra nasce desligada. Na primeira hora, pausa sozinha depois de 20 ações.
                    </>
                  ) : (
                    <>
                      O filtro decide se aquela conversa entra quando o evento acontecer.
                      Salvar a regra não altera as conversas que já existem.
                    </>
                  )}
                </Alert>

                {isScheduledEvent && form.condicoes?.schedule_interval !== undefined && (
                  <Accordion defaultExpanded sx={{ mb: 1, border: "2px solid", borderColor: "primary.main" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight={600}>
                        {eventSchema.schedule_interval?.label || "Intervalo de tempo"}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <DynamicFields
                        compact
                        schema={{ fields: { schedule_interval: eventSchema.schedule_interval } }}
                        values={{ ...form.condicoes }}
                        formState={form}
                        onChange={(name, value) =>
                          setForm((f) => ({
                            ...f,
                            condicoes: { ...(f.condicoes || {}), [name]: value },
                          }))
                        }
                        onLabel={(name, label) =>
                          setForm((f) => ({
                            ...f,
                            condicoes_labels: {
                              ...(f.condicoes_labels || {}),
                              [name]: label,
                            },
                          }))
                        }
                      />
                      <ScheduleRunWindowField
                        value={form.condicoes?.schedule_run_window}
                        onChange={(next) =>
                          setForm((f) => ({
                            ...f,
                            condicoes: {
                              ...(f.condicoes || {}),
                              schedule_run_window: next,
                            },
                          }))
                        }
                        onLabel={(label) =>
                          setForm((f) => ({
                            ...f,
                            condicoes_labels: {
                              ...(f.condicoes_labels || {}),
                              schedule_run_window: label,
                            },
                          }))
                        }
                      />
                    </AccordionDetails>
                  </Accordion>
                )}

                {isScheduledEvent && (
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(form.condicoes?.schedule_apply_all)}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              condicoes: { ...(f.condicoes || {}), schedule_apply_all: e.target.checked },
                            }))
                          }
                        />
                      }
                      label="Sem filtro: vale para todas as conversas abertas que entrarem nesta regra"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(form.condicoes?.schedule_include_closed)}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              condicoes: { ...(f.condicoes || {}), schedule_include_closed: e.target.checked },
                            }))
                          }
                        />
                      }
                      label="Incluir também concluída, perdida e cancelada"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(form.condicoes?.schedule_confirm_wide)}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              condicoes: { ...(f.condicoes || {}), schedule_confirm_wide: e.target.checked },
                            }))
                          }
                        />
                      }
                      label="Confirmo que a ação não volta atrás e pode atingir várias conversas"
                    />
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddCircleOutline />}
                    onClick={() => setConditionPickerOpen(true)}
                    disabled={!hasConditionPickerOptions}
                  >
                    Adicionar filtro
                  </Button>
                  {!hasConditionPickerOptions ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ alignSelf: "center" }}
                    >
                      Todos os filtros disponíveis já foram adicionados.
                    </Typography>
                  ) : null}
                </Box>

                <AutomationConditionPickerDialog
                  open={conditionPickerOpen}
                  onClose={() => setConditionPickerOpen(false)}
                  eventSchema={eventSchema}
                  usedKeys={usedKeys}
                  hiddenKeys={campaignConditionHiddenKeys}
                  onSelectFilter={addFilter}
                  onSelectClientPreset={addClientFieldPreset}
                />

                {!isScheduledEvent && Object.keys(form.condicoes || {}).length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Adicione pelo menos <b>um filtro</b> para evitar que a regra dispare em todas as
                    solicitações do evento escolhido.
                  </Alert>
                )}

                {Object.entries(form.condicoes || {})
                  .filter(
                    ([k]) =>
                      !isScheduleConditionMetaKey(k) &&
                      k !== LEGACY_CONDITION_USER_KEY &&
                      k !== LEGACY_CONDITION_AGENT_KEY
                  )
                  .map(([key, val], idx) => {
                  const def = eventSchema[key] || { label: key, type: "text" };
                  const titulo = getConditionAccordionTitle(
                    key,
                    def,
                    form.condicoes_labels?.[key]
                  );

                  return (
                    <Accordion key={key} defaultExpanded sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                            {idx + 1}. {titulo}
                          </Typography>
                          {form.condicoes_labels?.[key] && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              —
                              {Array.isArray(form.condicoes_labels[key])
                                ? ` ${form.condicoes_labels[key].join(", ")}`
                                : ` ${form.condicoes_labels[key]}`}
                            </Typography>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <DynamicFields
                          compact
                          schema={{ fields: { [key]: def } }}
                          schemaFull={{ fields: eventSchema }}
                          onlyKeys={[key]}
                          values={{ ...form.condicoes }}
                          formState={form}
                          onChange={(name, value) =>
                            setForm((f) => ({
                              ...f,
                              condicoes: { ...(f.condicoes || {}), [name]: value },
                            }))
                          }
                          onLabel={(name, label) =>
                            setForm((f) => ({
                              ...f,
                              condicoes_labels: {
                                ...(f.condicoes_labels || {}),
                                [name]: label,
                              },
                            }))
                          }
                          onValuesPatch={(patch) =>
                            setForm((f) => {
                              const next = { ...(f.condicoes || {}), ...patch };
                              if (patch.responsavel !== undefined) {
                                delete next.usuarios;
                                delete next.agentesdeia;
                              }
                              return { ...f, condicoes: next };
                            })
                          }
                          onLabelsPatch={(patch) =>
                            setForm((f) => ({
                              ...f,
                              condicoes_labels: {
                                ...(f.condicoes_labels || {}),
                                ...patch,
                              },
                            }))
                          }
                        />
                        <Box sx={{ mt: 1 }}>
                          <Button
                            color="error"
                            size="small"
                            startIcon={<DeleteOutline />}
                            onClick={() => removeFilter(key)}
                          >
                            Remover filtro
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                <Card
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                    bgcolor: "background.paper",
                  }}
                >
                <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="subtitle2">
                      Quem entra agora
                    </Typography>

                    {previewLoading ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">
                          Calculando impacto…
                        </Typography>
                      </Box>
                    ) : previewScope === "forward" ? (
                      <Alert severity="info">
                        <Typography fontWeight={600}>Só conversas criadas depois de ligar</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Nada é contado agora porque conversas que já existem ficam de fora. O primeiro ciclo espera o intervalo inteiro. A checagem pode atrasar até 1 minuto.
                        </Typography>
                      </Alert>
                    ) : previewCount != null ? (() => {
                      const alert = getImpactAlert(previewCount, previewScope);

                      return (
                        <Alert severity={alert.severity}>
                          <Typography fontWeight={600}>{alert.title}</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {alert.text}
                          </Typography>
                        </Alert>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">
                        Escolha um filtro para ver quem entraria agora.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* ===== 3 - Ações ===== */}
            {activeStep === 3 && (
              <>
                <Alert icon={<BoltIcon />} severity="info" sx={{ mb: 2 }}>
                  Inclua uma ou mais <b>ações</b>. Você pode reordenar, duplicar e remover.
                </Alert>

                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddCircleOutline />}
                    onClick={handleOpenAddAction}
                    disabled={!acoesCat.length}
                  >
                    Adicionar ação
                  </Button>
                </Box>

                <Menu
                  open={openAddAction}
                  onClose={handleCloseAddAction}
                  anchorEl={anchorAddAction}
                >
                  {acoesCat.map((a) => (
                    <MenuItem key={a.id} onClick={() => addAction(a.id)}>
                      <ListItemIcon>
                        <DoneAllIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={a.nome} secondary={a?.descricao || ""} />
                    </MenuItem>
                  ))}
                </Menu>

                {(form.acoes || []).length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Inclua ao menos uma ação (ex.: “Enviar email”).
                  </Alert>
                )}

                {(form.acoes || []).map((a, idx) => {
                  const def = acoesCat.find((x) => x.id === Number(a.id));
                  const title = def?.nome || `Ação #${a.id}`;

                  const pairs = Object.entries(a.labels || {});
                  const resumo = pairs.length
                    ? pairs
                        .slice(0, 2)
                        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
                        .join(" • ")
                    : "sem parâmetros";

                  return (
                    <Accordion key={idx} defaultExpanded={false} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                            {idx + 1}. {title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            — {resumo}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          display="flex"
                          gap={1}
                          alignItems="center"
                          sx={{ mb: 1, flexWrap: "wrap" }}
                        >
                          <TextField
                            select
                            label="Ação"
                            size="small"
                            margin="dense"
                            sx={{ minWidth: 260 }}
                            value={a.id}
                            onChange={(e) => {
                              const id = Number(e.target.value);
                              setForm((f) => {
                                const copy = [...f.acoes];
                                copy[idx] = { id, values: {}, labels: {} };
                                return { ...f, acoes: copy };
                              });
                            }}
                          >
                            {acoesCat.map((x) => (
                              <MenuItem key={x.id} value={x.id}>
                                {x.nome}
                              </MenuItem>
                            ))}
                          </TextField>

                          <Tooltip title="Duplicar">
                            <IconButton
                              onClick={() =>
                                setForm((f) => {
                                  const copy = [...(f.acoes || [])];
                                  copy.splice(idx + 1, 0, JSON.parse(JSON.stringify(a)));
                                  return { ...f, acoes: copy };
                                })
                              }
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Mover para cima">
                            <span>
                              <IconButton
                                disabled={idx === 0}
                                onClick={() =>
                                  setForm((f) => {
                                    const copy = [...(f.acoes || [])];
                                    const [it] = copy.splice(idx, 1);
                                    copy.splice(idx - 1, 0, it);
                                    return { ...f, acoes: copy };
                                  })
                                }
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Mover para baixo">
                            <span>
                              <IconButton
                                disabled={idx === (form.acoes || []).length - 1}
                                onClick={() =>
                                  setForm((f) => {
                                    const copy = [...(f.acoes || [])];
                                    const [it] = copy.splice(idx, 1);
                                    copy.splice(idx + 1, 0, it);
                                    return { ...f, acoes: copy };
                                  })
                                }
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Remover">
                            <IconButton
                              color="error"
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  acoes: f.acoes.filter((_, i) => i !== idx),
                                }))
                              }
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {def?.form ? (
                          <DynamicFields
                            compact
                            schema={def.form}
                            values={a.values || {}}
                            formState={form}
                            onChange={(name, value) =>
                              setForm((f) => {
                                const copy = [...f.acoes];
                                copy[idx] = {
                                  ...copy[idx],
                                  values: {
                                    ...(copy[idx].values || {}),
                                    [name]: value,
                                  },
                                };
                                return { ...f, acoes: copy };
                              })
                            }
                            onLabel={(name, label) =>
                              setForm((f) => {
                                const copy = [...f.acoes];
                                copy[idx] = {
                                  ...copy[idx],
                                  labels: {
                                    ...(copy[idx].labels || {}),
                                    [name]: label,
                                  },
                                };
                                return { ...f, acoes: copy };
                              })
                            }
                            onValuesPatch={(patch) =>
                              setForm((f) => {
                                const copy = [...f.acoes];
                                copy[idx] = {
                                  ...copy[idx],
                                  values: {
                                    ...(copy[idx].values || {}),
                                    ...patch,
                                  },
                                };
                                return { ...f, acoes: copy };
                              })
                            }
                            onLabelsPatch={(patch) =>
                              setForm((f) => {
                                const copy = [...f.acoes];
                                copy[idx] = {
                                  ...copy[idx],
                                  labels: {
                                    ...(copy[idx].labels || {}),
                                    ...patch,
                                  },
                                };
                                return { ...f, acoes: copy };
                              })
                            }
                          />
                        ) : (
                          <Alert severity="info">Esta ação não possui parâmetros.</Alert>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {errors.acoes && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.acoes}
                  </Alert>
                )}
              </>
            )}

            {/* ===== 4 - Resumo (legível) ===== */}
            {activeStep === 4 && (
              <Box>
                <Alert icon={<InfoOutlinedIcon />} severity="info" sx={{ mb: 2 }}>
                  {isScheduledEvent
                    ? `${selectedEventoEnriched?.meta?.description || ""} Desligada, nada acontece. Ligada, a checagem começa no próximo minuto. Na primeira hora, pausa depois de 20 ações.`
                    : "Ao salvar, a regra fica ligada e passa a valer só quando o evento acontecer, na conversa daquele momento."}
                </Alert>
                {isScheduledEvent && (
                  <FormControlLabel
                    sx={{ mb: 2 }}
                    control={
                      <Switch
                        checked={Boolean(form.ligarAgora)}
                        onChange={(e) => setForm((f) => ({ ...f, ligarAgora: e.target.checked }))}
                      />
                    }
                    label={form.ligarAgora ? "Ligar ao salvar: a checagem começa no próximo minuto" : "Salvar desligada: nenhuma conversa será alterada"}
                  />
                )}

                <Box sx={{ display: "grid", gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2">Descrição</Typography>
                    <Typography>{form.descricao || "—"}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Objeto</Typography>
                    <Chip label={selectedObjeto?.nome || "—"} size="small" />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Evento</Typography>
                    <Chip
                      label={selectedEventoEnriched?.meta?.displayTitle || selectedEvento?.nome || "—"}
                      size="small"
                    />
                    {selectedEventoEnriched?.meta?.timingLabel ? (
                      <Chip
                        label={selectedEventoEnriched.meta.timingLabel}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 0.5 }}
                      />
                    ) : null}
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Condições</Typography>

                    <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {Object.keys(form.condicoes_labels || {}).length ? (
                        Object.entries(form.condicoes_labels).map(([k, v]) => {
                          const niceKey =
                            eventSchema?.[k]?.label ||
                            (k === "schedule_run_window"
                              ? "Horário de execução"
                              : k === "schedule_interval"
                              ? eventSchema?.schedule_interval?.label || "Tempo de resposta"
                              : k);
                          const display = formatAutomationDisplayValue(v);
                          const full = Array.isArray(v)
                            ? `${niceKey}: ${v.map(formatAutomationDisplayValue).filter(Boolean).join(", ")}`
                            : `${niceKey}: ${display}`;
                          const short = compactListText(v, 2, 90);
                          const shown = short ? `${niceKey}: ${short}` : `${niceKey}: ${display || "—"}`;

                          return (
                            <Tooltip key={k} title={full}>
                              <Chip label={shown} size="small" sx={chipSx} />
                            </Tooltip>
                          );
                        })
                      ) : (
                        <Chip label="Nenhuma" size="small" />
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Ações</Typography>
                    <Box sx={{ display: "grid", gap: 1, mt: 0.5 }}>
                      {(form.acoes || []).length ? (
                        form.acoes.map((a, i) => {
                          const def = acoesCat.find((x) => x.id === Number(a.id));
                          const title = def?.nome || `Ação #${a.id}`;
                          const pairs = Object.entries(a.labels || {});
                          const resumo = pairs.length
                            ? pairs
                                .map(([k, v]) =>
                                  Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${v}`
                                )
                                .join(" • ")
                            : "sem parâmetros";

                          return (
                            <Box
                              key={`${a.id}-${i}`}
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 1,
                                bgcolor: "action.hover",
                              }}
                            >
                              <b>
                                {i + 1}. {title}
                              </b>
                              <Typography variant="body2" color="text.secondary">
                                {resumo}
                              </Typography>
                            </Box>
                          );
                        })
                      ) : (
                        <Chip label="Nenhuma" size="small" />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* navegação */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isFetching || submitting || isLoading}
              >
                Cancelar
              </Button>
              <Box display="flex" gap={2}>
                {activeStep > 0 && <Button onClick={handleBack}>Voltar</Button>}
                {activeStep < steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isFetching || submitting || isLoading}
                  >
                    Próximo
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isFetching || submitting || isLoading}
                  >
                    {isFetching || submitting || isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Salvar"
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* preview */}
        <Grid item xs={12} md={4} sx={{ width: "100%", flexShrink: 0, pr: 0 }}>
          <LivePreview
            objeto={selectedObjeto?.nome}
            evento={selectedEventoEnriched?.meta?.displayTitle || selectedEvento?.nome}
            condicoesLabels={form.condicoes_labels}
            acoesTitles={acoesTitles}
            eventSchema={eventSchema}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
